import * as GC from '@grapecity/spread-sheets';
import { getFont } from '@shared/spreadsheet/spreadsheet-config';
import { UNGROUPED_NAME, FAIL_COLOR } from './results-spread-config';
import { clone } from '@helpers/arrays';
import { getExogenousDisplayFormat } from '@components/model-structure/exogenous-lines/exogenous.spread.config';
import { ErrorsView } from '@models/results/results-class-library';
import { logError } from '../result-errors';
import * as SheetFunctions from '@helpers/spread/sheet-functions';
import { ResultsStoreService } from '@services/results/results-store.service';

let resultsStore: ResultsStoreService;

// Affects to both pages (summary & detail)
export function addExogenousByGroupID(
  currentRow: number,
  spread: GC.Spread.Sheets.Workbook,
  workSheet: GC.Spread.Sheets.Worksheet,
  exogenous: any = [],
  IgnoreErrors: boolean,
  col: number = 2,
  colShift: number = 1,
  DISFilterEnabled = false, // Display_In_Summary
  showOnlyLastPeriodValue = false, // TODO: Pending of implementation for the highlights after centralize both approaches
  _resultsStore: ResultsStoreService,
  resultSetID,
  mnemonic
) {
  if (!resultsStore) resultsStore = _resultsStore;

  currentRow++;

  // Clone the exogenous object to avoid unintended modifications to the result in the Store

  if (!exogenous) {
    exogenous = [];
  }

  const isSummaryView = workSheet.name().includes('-summary');

  if (DISFilterEnabled) {
    // Apply DIS Filtering (It shows only the Display_In_Summary === 'Yes' Exogenous Lines)
    exogenous = Object.keys(exogenous)
      .filter((exogName: string) => exogenous[exogName].Display_In_Summary)
      .map((exogName: string) => exogenous[exogName]);
  }

  if (Object.keys(exogenous).length > 0) {
    if (!isSummaryView) {
      workSheet.setValue(currentRow, col - 2, 'Exogenous Lines');
      workSheet.getCell(currentRow, col - 2).font(getFont('sm', true));
    }

    const groupIdDictionary = {};
    Object.keys(exogenous).forEach(exogKey => {
      const exog = exogenous[exogKey];

      try {
        exog.GroupID = exog.GroupID.trim();
      } catch (err) {
        console.error('exogenous-builder.ts:59', { exog, err });
      }

      // Ungrouped - Fix 3337
      if (exog.GroupID === '' || exog.GroupID === null || exog.GroupID === undefined) {
        exog.GroupID = UNGROUPED_NAME;
      }

      if (!groupIdDictionary[exog.GroupID]) {
        groupIdDictionary[exog.GroupID] = [];
      }

      const condition = !groupIdDictionary[exog.GroupID].find(_exog => _exog.Name === exogKey);
      if (condition) {
        groupIdDictionary[exog.GroupID].push(exog);
      }
    });

    const displayKRIRow = (groupID: string) => {
      currentRow++;
      // GroupID Title
      const cell = workSheet.getCell(currentRow, col);
      cell.value(groupID).font(getFont('sm', true));

      // Paint all the rows
      const testRiskLimitEnabled = true;
      const targetCol = col - 2;
      Object.keys(groupIdDictionary[groupID]).forEach((exogKey: string) => {
        // Ignore period 0 in summary view (but not in highlights nor detail views)
        const exog = clone(groupIdDictionary[groupID][exogKey]);
        if (isSummaryView) {
          delete exog.Results['0'];
        }

        const newRow = displayExogenousLine(
          currentRow,
          exog,
          spread,
          workSheet,
          targetCol,
          col + colShift,
          IgnoreErrors,
          testRiskLimitEnabled,
          resultSetID,
          mnemonic
        );
        currentRow = newRow;
      });
    };

    // Build each GroupID-based Table (ungrouped at the bottom as per Fix 3337)
    // Grouped
    Object.keys(groupIdDictionary)
      .filter(group => group !== UNGROUPED_NAME)
      .forEach((groupID: string) => displayKRIRow(groupID));
    // Ungrouped
    Object.keys(groupIdDictionary)
      .filter(group => group === UNGROUPED_NAME)
      .forEach((groupID: string) => displayKRIRow(groupID));
  }
  return currentRow;
}

// For Summary & Detail view
function displayExogenousLine(
  currentRow: number,
  exog: any,
  spread: GC.Spread.Sheets.Workbook,
  workSheet: GC.Spread.Sheets.Worksheet,
  column: number,
  outPropColumn: number,
  IgnoreErrors: boolean,
  testRiskLimitEnabled = false,
  resultSetID: number,
  mnemonic: string
) {
  currentRow++;
  const rowFontSize = 'x-sm';
  const isSummaryView = workSheet.name().includes('-summary');

  if (!isSummaryView) {
    const balanceSheetItemMnemonic = exog?.Mnemonic;
    workSheet.getCell(currentRow, 0).value(balanceSheetItemMnemonic);
    workSheet.getCell(currentRow, 1).value('EX');
  }

  // Name
  const exogNameColumn = column + 3;
  workSheet.getCell(currentRow, exogNameColumn).text(exog.Name).wordWrap(false).font(getFont(rowFontSize));

  // Values
  if (!isSummaryView) outPropColumn--;
  let k = 0;
  for (const res of Object.keys(exog.Results)) {
    const targetCol = outPropColumn + 3 + k;
    const numericValue = exog.Results[res].Value as number;
    const cell = workSheet.getCell(currentRow, targetCol);
    // Accounting formatting (part of Fix 3665) -- Uncompatible with ellipsis
    SheetFunctions.setOutputReportNumberFormatting(
      spread,
      workSheet,
      currentRow,
      targetCol,
      numericValue,
      getExogenousDisplayFormat(exog.Display_format),
      false
    );
    resultsStore.flagDisplayZeroAsForConversionOnExport(workSheet, cell);

    // RISK LIMIT TEST
    if (testRiskLimitEnabled) {
      const shouldFail = doesItFailsAtRiskLimitTest(numericValue, exog.Fail_Operator, exog.Risk_Limit);
      if (shouldFail) {
        cell.backColor(FAIL_COLOR);
      }
    }

    // Font and right alignment
    cell.font(getFont(rowFontSize));
    cell.hAlign(GC.Spread.Sheets.HorizontalAlign.right);

    // DISPLAY ERRORS
    if (exog.Results[res].HasError && !IgnoreErrors && !isSummaryView) {
      const error = new ErrorsView();
      for (const errorCode of exog.Results[res].ErrorCodes) {
        error.column = targetCol;
        error.row = currentRow;
        error.message = errorCode;
        SheetFunctions.addComment(workSheet, currentRow, targetCol, errorCode);
        logError(error, null, exog.Mnemonic, exog.Results[res].period, errorCode, '', 3, resultSetID, mnemonic);
      }
    }
    k++;
  }
  return currentRow;
}

/**
 * If the value compared using fail operator meets the condition, means that that exogenous line should fail.
 * If the compared value should fail It returns true. If not, returns false.
 */
export function doesItFailsAtRiskLimitTest(comparedValue: number, failOperator: string, riskLimit: number) {
  const valueIfTestPassed = false;

  return comparedValue === riskLimit
    ? // Same value than risk limit should NOT FAIL
      valueIfTestPassed
    : failOperator === '<'
    ? comparedValue < riskLimit
    : failOperator === '>'
    ? comparedValue > riskLimit
    : // If there is no Fail Operator, should NOT FAIL
      valueIfTestPassed;
}
